import { createClient } from "@supabase/supabase-js";

const url = process.env.REACT_APP_SUPABASE_URL;
const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  // Fail loudly in dev — the rest of the app is useless without Supabase.
  // eslint-disable-next-line no-console
  console.error("Missing REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_ANON_KEY");
}

/**
 * Custom fetch wrapper that buffers the response body so that any downstream
 * consumer (e.g. supabase-js calling `.json()` then `.text()` in error paths
 * — see supabase/supabase-js#688) cannot trigger
 *   `TypeError: Failed to execute 'text'/'json' on 'Response': body stream
 *    already read`
 * which surfaces on the K8s ingress under high latency.
 *
 * We pre-read the body once into a TextDecoder buffer and override the
 * `.text()` / `.json()` methods on the new Response so they're idempotent.
 */
const safeFetch = async (input, init) => {
  const res = await fetch(input, init);
  const buf = await res.arrayBuffer();
  return buildWrapped(buf, res.status, res.statusText, res.headers);
};

/**
 * Create a Response-shaped wrapper whose body methods are all idempotent.
 * `.clone()` returns a fresh equivalent wrapper so any consumer that
 * re-clones still gets a re-readable body.
 */
function buildWrapped(buf, status, statusText, headers) {
  const text = new TextDecoder().decode(buf);
  // 101/204/205/304 are "null body" statuses — passing any body (even empty) throws.
  const nullBody = [101, 204, 205, 304].includes(status);
  const w = new Response(nullBody ? null : buf, { status, statusText, headers });
  w.text        = () => Promise.resolve(text);
  w.json        = () => Promise.resolve(text ? JSON.parse(text) : null);
  w.arrayBuffer = () => Promise.resolve(buf.slice(0));
  w.blob        = () => Promise.resolve(new Blob([buf]));
  w.clone       = () => buildWrapped(buf, status, statusText, headers);
  return w;
}

export const supabase = createClient(url, anonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: window.localStorage,
  },
  global: { fetch: safeFetch },
});

export const SUPABASE_URL = url;
